import { useEffect, useMemo } from 'react';
import {
  UseFieldArrayReturn,
  UseFormReturn,
  useFieldArray,
  useForm,
} from 'react-hook-form';

import { useSelectedEmployeeId } from '~/hooks/employee';
import { useHasChanged } from '~/hooks/useHasChanged';

import { useGetCompanyForms } from '../SharedIndex/useGetCompanyForms';
import { useGetEmployeeForms } from '../SharedIndex/useGetEmployeeForms';

import {
  EmployeeDocumentsReadDataRow,
  mapDefaultData,
  readData,
} from './data/dataMapping';
export type FixForKeyIssue = {
  [key: string]: number | string | null | boolean | File | undefined;
};
export interface RowData extends FixForKeyIssue {
  fEmpFormId: number;
  fEmpFormEmpId: number;
  fEmpFormCompFormFK: number;
  fEmpFormCreatedTime: string;
  fEmpFormUpdatedTime: string;
  fEmpFormInfo?: string;
  fEmpFormAttachmentId: number;
  fEmpFormAttachmentName: string;
  fFormAttachment?: File | undefined;
}

export type IFormInput = {
  form: RowData[];
};

export type UseFormsOutput = {
  employeeId: number;
  readData: EmployeeDocumentsReadDataRow[];
  defaultData: RowData[];
  useFormMethods: UseFormReturn<IFormInput>;
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'form', 'id'>;
  isLoading: boolean;
};

export const useEmployeeDocuments = (): UseFormsOutput => {
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { data: employeeDocs, isLoading: isLoadingEmployeeDocs } =
    useGetEmployeeForms(selectedEmployeeId);
  const { data: companyForms, isLoading: isLoadingCompanyForms } =
    useGetCompanyForms();

  const isLoading = isLoadingEmployeeDocs || isLoadingCompanyForms;

  const useFormMethods = useForm<IFormInput>();
  const { control, reset } = useFormMethods;
  const useFieldArrayMethods = useFieldArray({ name: 'form', control });

  const defaultData = useMemo(
    () => mapDefaultData(employeeDocs),
    [employeeDocs],
  );
  const hasDefaultDataChanged = useHasChanged(JSON.stringify(defaultData));

  useEffect(() => {
    if (hasDefaultDataChanged) {
      reset({
        form: defaultData,
      });
    }
  }, [hasDefaultDataChanged, defaultData, reset]);

  const mappedReadData = useMemo(() => {
    return readData(employeeDocs, companyForms, selectedEmployeeId.toString());
  }, [employeeDocs, companyForms, selectedEmployeeId]);

  return {
    employeeId: selectedEmployeeId,
    readData: mappedReadData,
    defaultData,
    useFormMethods,
    useFieldArrayMethods,
    isLoading,
  };
};
